import React from 'react';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { buildCatalogListRequest } from 'src/app/routes/catalog/utils/buildCatalogListRequest';
import { useCurrentCity } from '@cp/entities/location';
import { CatalogMetaTags } from 'src/app/routes/catalog/components/meta';
import { useRouter } from '@cp/shared/router';
import { useGetQueryParams, useUpdateQueryParams } from '@cp/shared/queryParams';
import { useIsSubcatalog } from 'src/app/routes/catalog/model';
import { catalogLayoutSlice } from 'src/features/catalog';
import { useDispatch } from 'react-redux';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { CatalogViewMode, CatalogViewProps } from 'src/app/routes/catalog/types';
import { useIsMobile } from '@cp/ds/hooks/useIsMobile';
import { PageSkeleton } from '@cp/ds/components/pageSkeleton';
import loadable from '@loadable/component';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { CITY_PATH_PARAMETER, useClientWebResolver, useClientWebRouteProps } from '@cp/entities/clientWebRoutes';
import { useCatalogFilter } from 'src/app/routes/catalog/hooks/useCatalogFilter';
import { CatalogItemViewContext } from 'src/entities/catalog';
import { Resume } from 'src/widgets/resume';
import { VacancyView } from 'src/entities/vacancy';
import { BottomMenuWidget } from '#widgets/bottomMenu';
import { SubcatalogPage } from './subcatalog';

// @ts-ignore
const CatalogResumeMobileView = loadable(() => import('./view.resume.mobile'));
// @ts-ignore
const CatalogResumeDesktopView = loadable(() => import('./view.resume.desktop'));
// @ts-ignore
const CatalogVacancyMobileView = loadable(() => import('./view.vacancy.mobile'));
// @ts-ignore
const CatalogVacancyDesktopView = loadable(() => import('./view.vacancy.desktop'));

export const CatalogList = () => {
  const isMobile = useIsMobile();
  const router = useRouter();
  const dispatch = useDispatch();
  const resolver = useClientWebResolver();
  const pageProps = useClientWebRouteProps<ClientWebPage.Catalog>();
  const { currentCity } = useCurrentCity();
  const queryParams = useGetQueryParams();
  const listRequest = React.useMemo(
    () => buildCatalogListRequest({ pageProps, queryParams, cityId: Number(currentCity?.id) }),
    [currentCity?.id, pageProps, queryParams],
  );
  const updateQueryParams = useUpdateQueryParams();
  const { filter, setFilter } = useCatalogFilter({ pageProps, queryParams, updateQueryParams });
  const isSubcatalog = useIsSubcatalog();
  React.useEffect(
    () => () => {
      dispatch(catalogLayoutSlice.actions.setActiveItem(undefined));
    },
    [dispatch],
  );

  const handleViewModeChange = (newViewState: CatalogViewMode) => {
    dispatch(catalogLayoutSlice.actions.setDesktopViewMode(newViewState));
  };
  const handleChangeCity = React.useCallback(
    (newCity: CityViewModel) => {
      return router.navigate(
        resolver.resolve({
          page: ClientWebPage.Catalog,
          pageProps,
          queryParams,
          pathParams: { [CITY_PATH_PARAMETER]: newCity.alias },
        }),
      );
    },
    [resolver, router, pageProps, queryParams],
  );

  const View = isSubcatalog
    ? SubcatalogPage
    : pageProps.catalogType === CatalogItemType.Resume
      ? isMobile
        ? CatalogResumeMobileView
        : CatalogResumeDesktopView
      : isMobile
        ? CatalogVacancyMobileView
        : CatalogVacancyDesktopView;

  const pageLinkGenerator = React.useCallback(
    (targetPage: number) => {
      return String(
        resolver.resolve({
          page: ClientWebPage.Catalog,
          pageProps,
          queryParams: {
            ...queryParams,
            page: targetPage,
          },
        }),
      );
    },
    [resolver, pageProps, queryParams],
  );

  const handleChangePage = React.useCallback(
    (nextPage: number) => {
      return router.navigate(
        resolver.resolve({
          page: ClientWebPage.Catalog,
          pageProps,
          queryParams: { ...queryParams, page: nextPage },
          // pathParams: { [CITY_PATH_PARAMETER]: newCity.alias },
        }),
      );
    },
    [router, resolver, queryParams, pageProps],
  );

  const viewProps: CatalogViewProps = {
    currentCity,
    pageProps,
    listRequest,
    filter,
    onChangePage: handleChangePage,
    onChangeCity: handleChangeCity,
    onChangeFilter: setFilter,
    onChangeViewMode: handleViewModeChange,
    pageLinkGenerator,
  };

  return (
    <CatalogItemViewContext.Provider value={{ [CatalogItemType.Resume]: Resume, [CatalogItemType.Vacancy]: VacancyView }}>
      <CatalogMetaTags listRequest={listRequest} pageProps={pageProps} />
      <View {...viewProps} fallback={<PageSkeleton />} />
      <BottomMenuWidget />
    </CatalogItemViewContext.Provider>
  );
};
